import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HardHat, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate(location.state?.from || "/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
      <div className="hidden md:block relative border-r border-neutral-800">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1646324554833-f0b6a479fa5d)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-neutral-950/40" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-amber-500 grid place-items-center">
              <HardHat className="w-5 h-5 text-neutral-950" strokeWidth={2.5} />
            </div>
            <div className="display text-2xl">SITE<span className="text-amber-500">CREWS</span></div>
          </div>
          <div>
            <h2 className="display text-5xl leading-none">Back on <span className="text-amber-500">the tools.</span></h2>
            <p className="text-neutral-400 mt-4 max-w-sm">Sign in to post shifts, find crew, or line up your next job.</p>
          </div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Dublin · Meath · Louth · Monaghan</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Log In</div>
            <h1 className="display text-4xl mt-2">Punch In</h1>
          </div>

          {error && (
            <div data-testid="login-error" className="border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none px-4 py-3 text-neutral-100"
              placeholder="you@example.ie"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none px-4 py-3 text-neutral-100"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full bg-amber-500 text-neutral-950 font-bold uppercase tracking-widest py-3 hover:bg-amber-400 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? "Signing in…" : (<>Sign In <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <div className="text-center">
            <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500">Forgot password?</Link>
          </div>
          <div className="text-sm text-neutral-400 text-center">
            No account?{" "}
            <Link data-testid="switch-to-register" to="/register" className="text-amber-500 hover:underline">Register</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
